// Weather events on top of seasons: heatwaves and storms.
import { store, pushNews } from '../core/store.js';
import { bus } from '../core/bus.js';

// Daily chance per season of each event starting (if no event active)
const CHANCES = {
  summer: { heatwave: 0.04, storm: 0.010 },
  spring: { heatwave: 0.01, storm: 0.012 },
  autumn: { heatwave: 0.0,  storm: 0.020 },
  winter: { heatwave: 0.0,  storm: 0.015 },
};
const GRACE_DAYS = 90; // no disasters while the player is starting up

export class WeatherSystem {
  constructor(world) {
    this.world = world;
    this.current = null;       // { type: 'heatwave'|'storm', daysLeft }
    this._subs = [bus.on('sim:newDay', () => this.dailyTick())];
  }
  dispose() { this._subs.forEach((u) => u()); }

  // multipliers consumed elsewhere
  visitorMultiplier() {
    if (!this.current) return 1;
    return this.current.type === 'storm' ? 0.5 : 0.85;
  }
  thirstMultiplier() {
    return this.current?.type === 'heatwave' ? 2.2 : 1;
  }

  dailyTick() {
    const s = store.get();
    if (s.kidsMode) return; // kids mode: always friendly weather
    // total elapsed days since start (30-day months)
    const totalDays = (s.year - 1) * 360 + (s.month - 3) * 30 + s.day;
    if (totalDays < GRACE_DAYS) return;
    if (this.current) {
      this.current.daysLeft--;
      if (this.current.daysLeft <= 0) {
        pushNews(this.current.type === 'storm' ? 'The storm has passed.' : 'The heatwave has broken.', 'info');
        this._applyVisuals(null);
        this.current = null;
        store.set({ weather: null });
      }
      return;
    }
    const chances = CHANCES[s.season] || {};
    for (const [type, chance] of Object.entries(chances)) {
      if (Math.random() < chance) {
        this.current = { type, daysLeft: 1 + Math.floor(Math.random() * 2) };
        store.set({ weather: type });
        if (type === 'storm') {
          // storm damage: repair bill scales with built structures
          const world = this.world;
          const structures = world.build.paths.size + world.build.fences.size + world.amenities.items.length;
          const damage = Math.round(Math.min(2500, store.get().money * 0.04, structures * 5 * (0.5 + Math.random())));
          if (damage > 0) store.set({ money: store.get().money - damage });
          pushNews(`A storm hits the zoo — $${damage.toLocaleString('en-US')} in damages. Visitors stay home.`, 'error');
        } else {
          pushNews('Heatwave! Guests are thirsty — drink stands will do great business.', 'info');
        }
        this._applyVisuals(type);
        break;
      }
    }
  }

  _applyVisuals(type) {
    const r = this.world.renderer;
    const dim = type === 'storm' ? 0.35 : type === 'heatwave' ? 1.25 : 1;
    r._weatherDim = dim;
    if (r.sun) r.sun.intensity = 3.2 * dim;
    if (type === 'storm') {
      r.scene.fog.near = 150; r.scene.fog.far = 700;
    } else if (type === 'heatwave') {
      r._weatherDim = 1.25;
      r.scene.fog.near = 550; r.scene.fog.far = 2200;
    } else {
      r._weatherDim = 1;
      r.scene.fog.near = 550; r.scene.fog.far = 2200;
    }
  }
}
